import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface IncomeByRaceBarProps {
  location: string;
  onIncomeDifference?: (ratio: number | null) => void;
  embedOnly?: boolean;
}

// Bar chart: median income by race for latest year
const IncomeByRaceBar: React.FC<IncomeByRaceBarProps> = ({ location, onIncomeDifference, embedOnly = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);
  const [latestYear, setLatestYear] = React.useState<number | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Convert geofips to string and pad with leading zeros to match database format
    // median_income_by_race table stores geoid as TEXT with leading zeros (e.g., "0668154" not "668154")
    let geoid = String(geofips);
    // Pad to 7 digits for cities (place), 5 for counties, 2 for state
    if (/^\d+$/.test(geoid)) {
      if (geoid.length === 6) {
        // City geofips should be 7 digits with leading zero
        geoid = '0' + geoid;
      } else if (geoid.length === 4) {
        // County geofips should be 5 digits with leading zero
        geoid = '0' + geoid;
      } else if (geoid.length === 1) {
        // State geofips should be 2 digits with leading zero (e.g., "06" for California)
        geoid = '0' + geoid;
      }
    }

    // Fetch latest year available
    const { data: incYears } = await supabaseClient
      .from('median_income_by_race')
      .select('year')
      .eq('geoid', geoid)
      .order('year', { ascending: false })
      .limit(1);

    const year = incYears && incYears.length > 0 ? Number(incYears[0].year) : null;

    if (!year) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setLatestYear(year);

    const { data: inc } = await supabaseClient
      .from('median_income_by_race')
      .select('race_or_ethnicity, median_income')
      .eq('geoid', geoid)
      .eq('year', year)
      .order('median_income', { ascending: false });

    if (!inc || inc.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Log raw data for debugging - especially for San Luis Obispo
    if (location.toLowerCase().includes('san luis obispo') || location.toLowerCase().includes('slo')) {
      console.log(`🔍 IncomeByRaceBar - Raw data for ${location} (geoid: ${geoid}, year: ${year}):`, inc);
      const blackData = inc.find((r: any) => 
        r.race_or_ethnicity?.toLowerCase().includes('black') || 
        r.race_or_ethnicity?.toLowerCase().includes('african american')
      );
      if (blackData) {
        console.log(`🔍 Black/African American income data:`, {
          race: blackData.race_or_ethnicity,
          median_income: blackData.median_income,
          raw_value: blackData.median_income
        });
      }
    }

    setHasData(true);

    // Filter and validate income data
    // Median household income should be reasonable (between $0 and $500k)
    // Values outside this range are likely data quality issues
    // Also filter out values exactly at common census caps ($250k, $200k) which indicate data suppression
    const MAX_REASONABLE_INCOME = 500000;
    const MIN_REASONABLE_INCOME = 0;
    const CENSUS_CAPS = [250000, 200000]; // Common census income caps for privacy
    
    const validData = inc.filter((r: any) => {
      const income = Number(r.median_income || 0);
      // Filter out values outside reasonable range
      if (income < MIN_REASONABLE_INCOME || income > MAX_REASONABLE_INCOME) {
        return false;
      }
      // Filter out values exactly at census caps (likely data suppression)
      if (CENSUS_CAPS.includes(income)) {
        return false;
      }
      return true;
    });

    // Log warnings for filtered data
    const filteredOut = inc.filter((r: any) => {
      const income = Number(r.median_income || 0);
      if (income < MIN_REASONABLE_INCOME || income > MAX_REASONABLE_INCOME) {
        return true;
      }
      if (CENSUS_CAPS.includes(income)) {
        return true;
      }
      return false;
    });
    
    if (filteredOut.length > 0) {
      console.warn(`IncomeByRaceBar: Filtered out ${filteredOut.length} data point(s) with unrealistic values or census caps for ${location} (geoid: ${geoid}):`, 
        filteredOut.map((r: any) => ({
          race: r.race_or_ethnicity,
          income: r.median_income,
          year: year,
          reason: CENSUS_CAPS.includes(Number(r.median_income || 0)) ? 'census_cap' : 'out_of_range'
        }))
      );
    }

    if (validData.length === 0) {
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Deduplicate by race_or_ethnicity - if there are duplicates, keep the one with the highest income
    // This prevents duplicate labels in the chart
    const raceMap = new Map<string, { race: string; income: number }>();
    validData.forEach((r: any) => {
      const race = r.race_or_ethnicity || 'Unknown';
      const income = Number(r.median_income || 0);
      const existing = raceMap.get(race);
      if (!existing || income > existing.income) {
        raceMap.set(race, { race, income });
      }
    });

    // Convert map to arrays, sorted by income descending
    const uniqueData = Array.from(raceMap.values()).sort((a, b) => b.income - a.income);
    const races = uniqueData.map(d => d.race);
    const incomes = uniqueData.map(d => d.income);

    // Calculate income difference ratio (highest / lowest)
    if (onIncomeDifference && incomes.length > 0) {
      const validIncomes = incomes.filter(income => income > 0);
      if (validIncomes.length >= 2) {
        const highest = Math.max(...validIncomes);
        const lowest = Math.min(...validIncomes);
        const ratio = highest / lowest;
        onIncomeDifference(ratio);
      } else {
        onIncomeDifference(null);
      }
    }

    // Generate colors for bars
    const colors = races.map((_, idx) => {
      const hue = (idx * 137.508) % 360; // Golden angle for color distribution
      return `hsla(${hue}, 70%, 50%, 0.7)`;
    });

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: races,
        datasets: [
          {
            label: 'Median Household Income',
            data: incomes,
            backgroundColor: colors,
            borderColor: colors.map(c => c.replace('0.7', '1')),
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 60, // Extra padding for rotated x-axis labels
            top: 10,
            left: 10,
            right: 10
          }
        },
        plugins: {
          legend: embedOnly ? { display: false } : standardLegend,
          title: embedOnly ? { display: false } : standardTitle(`Median Household Income by Race - ${location}`),
          tooltip: {
            callbacks: {
              label: (ctx: any) => `$${(ctx.parsed.y || 0).toLocaleString()}`,
            }
          }
        },
        scales: {
          y: {
            title: { display: true, text: 'Median Household Income' },
            beginAtZero: true,
          },
          x: {
            title: { display: true, text: 'Race/Ethnicity', padding: { top: 10 } },
            ticks: { autoSkip: false, maxRotation: 45, minRotation: 0, padding: 5 },
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });

    creatingRef.current = false;
  }, [location, onIncomeDifference]);

  useEffect(() => {
    buildChart();
    
    // Handle resize to update chart
    const handleResize = () => {
      if (chartRef.current && canvasRef.current) {
        chartRef.current.resize();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasRef.current) {
          const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No income by race data available for {location}.</p>
      </div>
    );
  }

  return (
    <div className="chart-container" style={{ height: '100%', width: '100%', position: 'relative' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default IncomeByRaceBar;

